import styles from './CalculatedMeasuresDialog.module.css';

import React, { useEffect, useMemo, useState } from 'react';

import Dialog from 'components/ui/Dialog';
import Api from 'components/Api';
import CrudPageLayout from 'components/bng/pages/common/layout/CrudPageLayout';
import Icon from 'components/ui/common/Icon';
import BngIconButton from 'components/bng/ui/BngIconButton';
import BngDropdown from 'components/bng/ui/BngDropdown';
import CreateCalculatedMeasureDialog from 'components/bng/pages/admin/structures/calculatedMeasures/CreateCalculatedMeasureDialog';
import { MODALS } from 'components/ui/redux/Actions';
import useReduxDispatch from 'components/hooks/useReduxDispatch';
import UiMsg from 'components/ui/UiMsg';
import MetadataConfigurationDialog from 'components/bng/pages/admin/structures/bimQuery/menuTabs/MetadataConfigurationDialog';
import useTranslation from 'components/hooks/useTranslation';
import Utils from 'components/Utils';
import OpTextConfirmation from 'components/ui/OpTextConfirmation';

export default function CalculatedMeasuresDialog({ closeModal = _.noop, inMemoryId = 0 }) {
  const { t } = useTranslation();
  const dispatch = useReduxDispatch();

  const [calculatedMeasures, setCalculatedMeasures] = useState([]);
  const [cubeMeasures, setCubeMeasures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await Api.Origin.CalculatedMeasure.findAll(inMemoryId);
      const { measures } = await Api.Origin.CalculatedMeasure.findStructure(inMemoryId);
      setCalculatedMeasures(data);
      setCubeMeasures(measures);
    } catch (e) {
      console.error('Error on findCalculatedMeasures()', await e);
      UiMsg.ajaxError(null, e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredMeasures = useMemo(() => {
    if (_.isEmpty(filter)) {
      return calculatedMeasures;
    }

    const lowerFilter = filter.toLowerCase();
    return calculatedMeasures.filter(
      (cm) =>
        cm.name?.toLowerCase().includes(lowerFilter) ||
        cm.caption?.toLowerCase().includes(lowerFilter) ||
        cm.dimension?.toLowerCase().includes(lowerFilter) ||
        Utils.Users.displayName(cm.createdBy)?.toLowerCase().includes(lowerFilter) ||
        Utils.Users.displayName(cm.updatedBy)?.toLowerCase().includes(lowerFilter)
    );
  }, [calculatedMeasures, filter]);

  const openEditDialog = (calculatedMeasure) => {
    return dispatch(
      MODALS.open(CreateCalculatedMeasureDialog, {
        calculatedMeasure,
        calculatedMeasures: calculatedMeasures,
        inMemoryId,
        onChange: fetchData,
      })
    );
  };

  const allMeasures = useMemo(() => {
    const cube = cubeMeasures.map((m) => ({
      id: m.value,
      caption: m.description,
    }));

    const calculated = calculatedMeasures
      .filter((m) => m.dimension === 'Measures')
      .map((m) => ({
        id: m.id,
        caption: m.caption || m.name,
      }));

    return [...cube, ...calculated];
  }, [cubeMeasures, calculatedMeasures]);

  const tableColumns = useMemo(
    () => [
      {
        size: '1%',
        render: (row, idx, rowProps, dragProps) => (
          <div className={`${styles.draggableIcon}`} {...dragProps.dragHandleProps}>
            <Icon icon="drag_indicator" />
          </div>
        ),
        rowClassName: styles.draggableTd,
      },
      {
        label: t('name'),
        render: (item) => {
          return <div className={`${styles.labelStyle}`}>{item.name}</div>;
        },
        rowClassName: styles.nameTd,
      },
      {
        label: t('newInMemoryVisualizationField'),
        render: (item) => <div className={`${styles.labelStyle}`}>{item.caption}</div>,
        rowClassName: styles.nameTd,
      },
      {
        label: t('dimension'),
        render: (item) => <div className={`${styles.labelStyle}`}>{item.dimension}</div>,
        rowClassName: styles.nameTd,
      },
      {
        label: t('createdBy'),
        render: (item) => {
          if (!item.createdBy) {
            return null;
          }

          return (
            <div className={`${styles.labelStyle}`}>
              {Utils.Users.displayName(item.createdBy)}
              <Icon icon="info_outline" title={`${t('created.at')} ${Utils.Date.formatDateTime(item.createdAt)}`} />
            </div>
          );
        },
        rowClassName: styles.nameTd,
      },
      {
        label: t('modifiedBy'),
        render: (item) => {
          if (!item.updatedBy) {
            return null;
          }

          return (
            <div className={`${styles.labelStyle}`}>
              {Utils.Users.displayName(item.updatedBy)}
              <Icon icon="info_outline" title={`${t('updated.on')} ${Utils.Date.formatDateTime(item.updatedAt)}`} />
            </div>
          );
        },
        rowClassName: styles.nameTd,
      },
      {
        label: t('actions'),
        headerClassName: `${styles.actionsHeader}`,
        render: (item) => {
          return (
            <div className={`${styles.actionsButtonsWrapper}`}>
              <BngDropdown
                icon="more_vert"
                options={[
                  {
                    icon: 'edit',
                    label: t('edit'),
                    onClick: () => openEditDialog(item),
                  },
                  {
                    icon: 'delete_outline',
                    label: t('delete'),
                    onClick: async () => {
                      OpTextConfirmation({
                        msg: { t },
                        title: t('attention'),
                        html: `${t('confirm.delete.calculated.measure', [`<b>${item.caption}</b>`])}`,
                        onConfirm: async () => {
                          try {
                            await Api.Origin.CalculatedMeasure.remove(inMemoryId, item.id);
                            await fetchData();
                            UiMsg.ok(t('InMemoryCalculatedMeasureRemoved'));
                          } catch (e) {
                            console.error('Error on deleteCalculatedMeasure()', e);
                            UiMsg.ajaxError(null, e);
                          }
                        },
                        disableInput: true,
                      });
                    },
                  },
                ]}
                popperClassName={`${styles.dropdownPopperClassName} `}
              />
              {item.dimension === 'Measures' && (
                <>
                  <BngIconButton
                    icon={<img src={Api.buildUrl('/resources/images/ada-ai/ada-ai-icon.png')} alt="Ada AI" />}
                    className={`AdaAi ${styles.AdaAi}`}
                    onClick={() => {
                      dispatch(
                        MODALS.open(MetadataConfigurationDialog, {
                          dimensionType: 'Measure',
                          caption: item.caption,
                          measures: allMeasures
                            .filter((cm) => cm.id !== item.id)
                            .map((cm) => ({ fieldName: cm.id, caption: cm.caption })),
                          metadata: () => {
                            return _.cloneDeep(item.props.metadata);
                          },
                          onSave: async (values) => {
                            try {
                              const idx = calculatedMeasures.findIndex((c) => c.id === item.id);
                              const measure = _.cloneDeep(calculatedMeasures[idx]);
                              measure.props.metadata = values;
                              measure.props.metadata.empty = false;
                              await Api.Origin.CalculatedMeasure.save(inMemoryId, measure, 'METADATA');

                              const copy = calculatedMeasures.slice();
                              copy[idx] = measure;
                              setCalculatedMeasures(copy);
                            } catch (e) {
                              console.error('Error on updateCalculatedMeasureMetadata()', e);
                              UiMsg.ajaxError(null, e);
                            }
                          },
                        })
                      );
                    }}
                  />
                  {!item.props.metadata.empty && <Icon icon="check_circle" className={`${styles.metadataCheckMark}`} />}
                </>
              )}
            </div>
          );
        },
      },
    ],
    [inMemoryId, calculatedMeasures]
  );

  return (
    <Dialog
      onClose={closeModal}
      title={t('calculated.measures')}
      className={`CalculatedMeasuresDialog ${styles.dialog}`}
      newDialogLayout={true}
    >
      <BngIconButton
        icon="add_circle"
        className={`${styles.createMeasureButton}`}
        text={t('create.new.measure')}
        onClick={() => openEditDialog()}
      />
      <CrudPageLayout
        className={`${styles.crudPageLayout}`}
        loading={loading}
        tableColumns={tableColumns}
        tableRows={filteredMeasures}
        hasRefresh={false}
        onDropHandler={async (result) => {
          if (!result.destination) {
            return;
          }

          let fromIdx = result.source.index;
          let toIdx = result.destination.index;

          if (fromIdx === toIdx) {
            return;
          }

          const from = filteredMeasures[fromIdx];
          const to = filteredMeasures[toIdx];

          fromIdx = calculatedMeasures.findIndex((m) => m.id === from.id);
          toIdx = calculatedMeasures.findIndex((m) => m.id === to.id);

          if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) {
            return;
          }

          try {
            await Api.Origin.CalculatedMeasure.changeOrder(inMemoryId, fromIdx, toIdx);
            fetchData();
          } catch (e) {
            console.error('Error on changeCalculatedMeasureOrder()', e);
            UiMsg.ajaxError(null, e);
          }
        }}
        onSearchChanged={setFilter}
      />
    </Dialog>
  );
}
